<script setup>
import { ref, watch, computed, nextTick, onMounted, onUnmounted } from "vue"

import MenuBar from "@/components/MenuBar.vue"
import LeftBar from "@/components/LeftBar.vue"
import HeaderBar from "@/components/HeaderBar.vue"
import MapBox from "@/components/MapBox.vue"
import RightBar from "@/components/RightBar.vue"
import CoordinateConverterDialog from "@/components/CoordinateConverterDialog.vue"
import SettingsDialog from "@/components/SettingsDialog.vue"
import TutorialTour from "@/components/TutorialTour.vue"
import { emptyFeatureCollection, parseImportedText, featureCollectionToKml } from "@/utils/interop"
import { featureCollectionToCsv } from "@/utils/csvExport"
import { removeDuplicatePoints } from "@/utils/dedup"
import { exportMapPdf } from "@/utils/pdfExport"
import { loadUserPreferences, saveUserPreferences } from "@/utils/userPreferences"
import { appendFeatureCollection } from "@/utils/featureAppend"
import { COORDINATE_FORMAT_OPTIONS, formatCoordinatePair } from "@/utils/coordinateFormat"

const FEATURE_CAP = 500

const mapRef = ref(null)
const fileInputRef = ref(null)
const importFileInputRef = ref(null)
let fileOperationGeneration = 0
let activeFileReader = null
let mapRenderGeneration = 0
const readyMaps = new WeakSet()
const appError = ref("")
const appInfo = ref("")
const projectName = ref("magpie-project")
const manualPolygonActive = ref(false)
const manualPolygonDraft = ref([])
const buildVersion = __BUILD_VERSION__
const buildDate = __BUILD_DATE__
const showAboutModal = ref(false)
const showCoordinateConverter = ref(false)
const showSettings = ref(false)
const settingsError = ref("")
const preferences = ref(loadUserPreferences())
const showTutorial = ref(false)
const modalCloseRef = ref(null)
const iconLoadFailures = ref(0)
const aboutEmail = "magpie@stephenson.link"
const DEMO_KML_URL = `${import.meta.env.BASE_URL}samples/demo-magpie.kml`
const PADDLE_BLANK_ICON_COLORS = ["red", "blu", "grn", "ylw", "ltblu", "pink", "purple", "wht"]
const ABOUT_SAMPLE_LAT = -32.79716666666667
const ABOUT_SAMPLE_LNG = 151.83316666666667
const coordinateFormatSamples = COORDINATE_FORMAT_OPTIONS.map((option) => ({
  label: option.label,
  value: formatCoordinatePair(ABOUT_SAMPLE_LAT, ABOUT_SAMPLE_LNG, option.value),
}))

const TUTORIAL_STEPS = Object.freeze([
  {
    target: '[data-tutorial="map"]',
    title: "Map navigation",
    body: "Drag to pan the map, then use the mouse wheel or map controls to zoom.",
    side: "bottom",
  },
  {
    target: '[data-tutorial="point-panel"]',
    title: "Add a point",
    body: "Enter one coordinate, choose an icon, and add the point to the map.",
    side: "right",
  },
  {
    target: '[data-tutorial="path-panel"]',
    title: "Add a path",
    body: "Enter a coordinate on each line and use Type to choose the path geometry.",
    side: "right",
  },
  {
    target: '[data-tutorial="polygon-panel"]',
    title: "Add a polygon",
    body: "Enter a coordinate list or use freestyle drawing to create an area.",
    side: "right",
  },
  {
    target: '[data-tutorial="layers"]',
    title: "Manage layers",
    body: "Select features here to zoom to, edit, or delete them.",
    side: "left",
  },
  {
    target: '[data-tutorial="file-menu"]',
    title: "Open or import data",
    body: "Open replaces the current project and adopts the selected file; Import Features appends features and preserves the current project.",
    side: "bottom",
  },
  {
    target: '[data-tutorial="file-menu"]',
    title: "Export work",
    body: "Use File → Export to export your work as KML, GeoJSON, PDF, or CSV.",
    side: "bottom",
  },
])

const MAGPIE_ONE_LINERS = [
  "oi human, that sandwich now belongs to me.",
  "i saw your shiny keys. tribute accepted.",
  "back away slowly, this footpath is sovereign.",
  "nice helmet, still gonna swoop your soul.",
  "i collect zip ties and your dignity.",
  "your lawn? incorrect. magpie runway.",
  "i scream therefore i am king.",
  "drop a chip and nobody gets pecked.",
  "that ute looked at me funny.",
  "i outsourced chaos to my cousins.",
  "my beak has opinions about your hair.",
  "territory expands whenever i feel dramatic.",
  "i'm not angry, i'm performance art.",
  "your bike bell summons my battle mode.",
  "i nap only between crimes.",
  "look away, i'm plotting weather-based mischief.",
  "i filed a complaint with the gumtree.",
  "you call it screeching; i call diplomacy.",
  "that breadcrumb was a legally binding gift.",
  "i am the reason spring needs warning signs.",
  "your picnic has entered the magpie tax zone.",
  "i trained the wind to carry insults.",
  "give me one chip and i spare your mirror.",
  "my shadow is a licensed emotional support menace.",
  "i brought vibes, violence, and impeccable timing.",
  "chirr warbloo weeoo krrt krrt.",
  "wark wark blip blip skreeee.",
  "plooop chirrik kaweeooo trrk.",
  "skrraaa wee-chonk warb warb.",
  "krik krik bwooplaa zeee.",
  "chonk-ree chirr-bonk waaaoo.",
  "tzee tzee warbloop krrr.",
  "brapchirp weeeonk grrk grrk.",
  "woobloo kyaa kyaa trrri.",
  "skronk weeoo plink plink.",
  "chirrpadoodle warkadoink zree.",
  "krraaa bonk-bonk weeooo.",
  "tinkchirp warrrk flooble.",
  "greeble chirr skree bop.",
  "bwaark tzeeonk prrrip prrrip.",
  "wibble-warb chirrk ka-chow.",
  "squarkle weeoo trrrk blap.",
  "plinky skraaa chirrboing.",
  "kawee kawee dronk chirrr.",
  "warksnort tzee tzee ploink.",
  "boopchirp skreeonk warblet.",
  "trrrwaaap chirr chonkle.",
  "kyaaa blorpchirp wreee.",
  "skwibble wark wark tzee.",
  "chirr-chaos bwoink grrraaa.",
]
const randomTagline = MAGPIE_ONE_LINERS[Math.floor(Math.random() * MAGPIE_ONE_LINERS.length)]

const geojsonData = ref(emptyFeatureCollection())
const featureCount = computed(() => geojsonData.value.features.length)
const duplicatePointCount = computed(() => {
  const seen = new Set()
  let duplicates = 0
  for (const f of geojsonData.value.features) {
    if (f?.geometry?.type !== "Point") continue
    const [lng, lat] = f.geometry.coordinates || []
    const key = `${Number(lat).toFixed(7)},${Number(lng).toFixed(7)}`
    if (seen.has(key)) duplicates += 1
    else seen.add(key)
  }
  return duplicates
})

function onMapLoaded(map) {
  mapRef.value = map
  if (map.loaded()) readyMaps.add(mapRef.value)
  map.on("click", onMapClick)
  renderGeoJsonToMap()
}

function onMapClick(e) {
  if (!manualPolygonActive.value) return
  manualPolygonDraft.value = [...manualPolygonDraft.value, [e.lngLat.lng, e.lngLat.lat]]
  renderDraftToMap()
}

async function renderGeoJsonToMap() {
  const generation = ++mapRenderGeneration
  const map = mapRef.value
  const featureSnapshot = cloneFeatureCollection(geojsonData.value)
  const draftSnapshot = manualPolygonDraft.value.map((coordinate) => [...coordinate])
  if (!map) return
  if (!readyMaps.has(map)) {
    map.once("load", () => {
      if (!ownsMapRender(map, generation) || !map.loaded()) return
      readyMaps.add(map)
      renderMapSnapshot(map, featureSnapshot, draftSnapshot, generation)
    })
    return
  }

  await renderMapSnapshot(map, featureSnapshot, draftSnapshot, generation)
}

function ownsMapRender(map, generation) {
  return mapRef.value === map && mapRenderGeneration === generation
}

async function renderMapSnapshot(map, featureSnapshot, draftSnapshot, generation) {
  if (!ownsMapRender(map, generation) || !readyMaps.has(map)) return

  ensureGeoJsonLayers(map)
  if (!ownsMapRender(map, generation)) return
  writeDraftToMap(map, draftSnapshot, generation)
  const result = await prepareFeatureCollectionForMap(
    map,
    featureSnapshot,
    () => ownsMapRender(map, generation),
  )
  if (!result || !ownsMapRender(map, generation)) return

  const { featureCollection: prepared, failureCount } = result
  if (!ownsMapRender(map, generation)) return
  iconLoadFailures.value = failureCount

  const src = map.getSource("draw-source")
  if (src && ownsMapRender(map, generation)) src.setData(prepared)

  const labelSrc = map.getSource("draw-label-source")
  if (labelSrc && ownsMapRender(map, generation)) {
    labelSrc.setData(buildLabelFeatureCollection(prepared))
  }
}

function openTutorial() {
  document.querySelector('[data-tutorial="help-menu"]')?.focus()
  showTutorial.value = true
}

function openSettings() {
  settingsError.value = ""
  showSettings.value = true
}

async function closeSettings() {
  showSettings.value = false
  settingsError.value = ""
  await nextTick()
  document.querySelector('[data-menu="tools"]')?.focus()
}

async function commitSettings({ coordinateFormat }) {
  settingsError.value = ""
  try {
    preferences.value = saveUserPreferences({ coordinateFormat })
    await closeSettings()
  } catch (error) {
    settingsError.value = error?.message || "Could not save settings in this browser."
  }
}

function handleMenuAction({ action }) {
  const handlers = {
    "file:new": () => newFile(),
    "file:open": () => openFile(),
    "file:import-features": () => importFeatures(),
    "file:export-kml": () => exportKml(),
    "file:export-geojson": () => exportGeoJson(),
    "file:export-pdf": () => exportPdf(),
    "file:export-csv": () => exportCsv(),
    "help:tutorial": () => openTutorial(),
    "help:about": () => showAbout(),
    "help:load-demo-kml": () => loadDemoKml(),
    "tools:add-vertices": () => addVerticesTool(),
    "tools:remove-duplicate-points": () => removeDuplicatePointsTool(),
    "tools:coordinate-converter": () => { showCoordinateConverter.value = true },
    "tools:settings": () => openSettings(),
  }
  handlers[action]?.()
}

function showAbout() {
  showAboutModal.value = true
}

function closeAbout() {
  showAboutModal.value = false
}

/* --- Theme (dark mode) toggle --- */
const theme = ref(
  (typeof localStorage !== "undefined" && localStorage.getItem("magpie-theme")) || "auto"
)
function applyTheme(value) {
  const root = document.documentElement
  if (value === "auto") {
    root.removeAttribute("data-theme")
  } else {
    root.setAttribute("data-theme", value)
  }
}
function toggleTheme() {
  const prefersDark =
    typeof matchMedia !== "undefined" && matchMedia("(prefers-color-scheme: dark)").matches
  const current = theme.value === "auto" ? (prefersDark ? "dark" : "light") : theme.value
  theme.value = current === "dark" ? "light" : "dark"
  if (typeof localStorage !== "undefined") localStorage.setItem("magpie-theme", theme.value)
  applyTheme(theme.value)
}

/* --- Global key handling (Esc closes modal) --- */
function onKeydown(e) {
  if (e.key === "Escape" && showAboutModal.value) closeAbout()
  if (e.key === "Escape" && showCoordinateConverter.value) showCoordinateConverter.value = false
  if (e.key === "Escape" && showSettings.value) closeSettings()
}
onMounted(() => {
  applyTheme(theme.value)
  window.addEventListener("keydown", onKeydown)
})
onUnmounted(() => {
  invalidateFileOperation()
  mapRenderGeneration += 1
  mapRef.value = null
  window.removeEventListener("keydown", onKeydown)
})

// Auto-focus the Close button when the modal opens (a11y: focus management)
watch(showAboutModal, (val) => {
  if (val) {
    // nextTick not needed — watch fires after DOM update with flush:'post' default
    // but the v-if means the element may not exist yet; use a small timeout.
    setTimeout(() => modalCloseRef.value?.focus(), 50)
  }
})

function newFile() {
  invalidateFileOperation()
  appError.value = ""
  appInfo.value = "New project created."
  geojsonData.value = emptyFeatureCollection()
  renderGeoJsonToMap()
}

function openFile() {
  beginFileSelection()
  fileInputRef.value?.click()
}

function importFeatures() {
  beginFileSelection()
  importFileInputRef.value?.click()
}

function invalidateFileOperation() {
  fileOperationGeneration += 1
  const reader = activeFileReader
  activeFileReader = null
  if (reader && typeof reader.abort === "function") {
    try {
      reader.abort()
    } catch {
      // Invalidating ownership is sufficient even when a reader cannot be aborted.
    }
  }
  return fileOperationGeneration
}

function beginFileSelection() {
  invalidateFileOperation()
  appError.value = ""
  appInfo.value = ""
}

function ownsFileOperation(reader, generation) {
  return reader !== null && activeFileReader === reader && fileOperationGeneration === generation
}

function releaseFileOperation(reader, generation) {
  if (ownsFileOperation(reader, generation)) activeFileReader = null
}

function onFileChosen(e) {
  const generation = invalidateFileOperation()
  appError.value = ""
  appInfo.value = ""

  const file = e.target.files?.[0]
  let reader = null
  try {
    if (!file) return

    reader = new FileReader()
    activeFileReader = reader
    reader.onload = () => {
      if (!ownsFileOperation(reader, generation)) return

      try {
        const text = String(reader.result || "")
        const normalized = parseImportedText(text, file.name)
        if (!ownsFileOperation(reader, generation)) return
        if (normalized.features.length > FEATURE_CAP) {
          appError.value = `Import has ${normalized.features.length} features, above cap (${FEATURE_CAP}).`
          return
        }

        geojsonData.value = normalized
        renderGeoJsonToMap()
        if (normalized.features.length) onZoomToFeature(normalized.features[0])

        const unit = normalized.features.length === 1 ? "feature" : "features"
        appInfo.value = `Imported ${file.name} (${geojsonData.value.features.length} ${unit}).`
        projectName.value = file.name.replace(/\.(kml|geojson|json)$/i, "") || "magpie-project"
      } catch (err) {
        if (ownsFileOperation(reader, generation)) {
          appError.value = `Failed to open file: ${err?.message || "unknown error"}`
        }
      } finally {
        releaseFileOperation(reader, generation)
      }
    }
    reader.onerror = () => {
      if (!ownsFileOperation(reader, generation)) return
      appError.value = `Failed to open file: ${reader.error?.message || "unknown error"}`
      releaseFileOperation(reader, generation)
    }
    reader.onabort = () => {
      if (!ownsFileOperation(reader, generation)) return
      appError.value = "Failed to open file: file read aborted"
      releaseFileOperation(reader, generation)
    }
    reader.readAsText(file)
  } catch (err) {
    if ((!reader && fileOperationGeneration === generation) || ownsFileOperation(reader, generation)) {
      appError.value = `Failed to open file: ${err?.message || "unknown error"}`
      releaseFileOperation(reader, generation)
    }
  } finally {
    e.target.value = ""
  }
}

function onImportFileChosen(e) {
  const generation = invalidateFileOperation()
  appError.value = ""
  appInfo.value = ""

  const file = e.target.files?.[0]
  let reader = null
  try {
    if (!file) return

    reader = new FileReader()
    activeFileReader = reader
    reader.onload = () => {
      if (!ownsFileOperation(reader, generation)) return
      try {
        const normalized = parseImportedText(String(reader.result || ""), file.name)
        if (!ownsFileOperation(reader, generation)) return
        if (!normalized.features.length) {
          appInfo.value = `No features found in ${file.name}.`
          return
        }

        const result = appendFeatureCollection(geojsonData.value, normalized.features, {
          featureCap: FEATURE_CAP,
          regenerateIds: true,
        })
        if (!result.ok) {
          appError.value = `Could not import features: ${result.error}`
          return
        }

        geojsonData.value = result.featureCollection
        renderGeoJsonToMap()
        zoomToFeatures(result.addedFeatures)
        const unit = result.addedFeatures.length === 1 ? "feature" : "features"
        appInfo.value = `Imported ${result.addedFeatures.length} ${unit} from ${file.name}.`
      } catch (err) {
        if (ownsFileOperation(reader, generation)) {
          appError.value = `Failed to import features: ${err?.message || "unknown error"}`
        }
      } finally {
        releaseFileOperation(reader, generation)
      }
    }
    reader.onerror = () => {
      if (!ownsFileOperation(reader, generation)) return
      appError.value = `Failed to import features: ${reader.error?.message || "unknown error"}`
      releaseFileOperation(reader, generation)
    }
    reader.onabort = () => {
      if (!ownsFileOperation(reader, generation)) return
      appError.value = "Failed to import features: file read aborted"
      releaseFileOperation(reader, generation)
    }
    reader.readAsText(file)
  } catch (err) {
    if ((!reader && fileOperationGeneration === generation) || ownsFileOperation(reader, generation)) {
      appError.value = `Failed to import features: ${err?.message || "unknown error"}`
      releaseFileOperation(reader, generation)
    }
  } finally {
    e.target.value = ""
  }
}

function assertCanExport() {
  if (!geojsonData.value.features.length) {
    appError.value = "Cannot export an empty project. Add at least one feature first."
    return false
  }
  appError.value = ""
  return true
}

function exportGeoJson() {
  if (!assertCanExport()) return
  const text = JSON.stringify(geojsonData.value, null, 2)
  const safeName = sanitizeProjectName(projectName.value)
  downloadText(text, `${safeName}.geojson`, "application/geo+json")
  appInfo.value = "GeoJSON export complete."
}

function exportKml() {
  if (!assertCanExport()) return
  const kmlText = featureCollectionToKml(geojsonData.value)
  const safeName = sanitizeProjectName(projectName.value)
  downloadText(kmlText, `${safeName}.kml`, "application/vnd.google-earth.kml+xml")
  appInfo.value = "KML export complete."
}

function exportCsv() {
  if (!assertCanExport()) return
  appInfo.value = ""
  try {
    const csvText = featureCollectionToCsv(geojsonData.value)
    const safeName = sanitizeProjectName(projectName.value)
    downloadText(csvText, `${safeName}.csv`, "text/csv;charset=utf-8")
    appInfo.value = "CSV export complete."
  } catch (err) {
    appError.value = `CSV export failed: ${err?.message || "unknown error"}`
  }
}

async function exportPdf() {
  if (!mapRef.value) {
    appError.value = "Map is not ready yet. Please wait for the map to load."
    return
  }
  appError.value = ""
  appInfo.value = "Preparing PDF…"
  try {
    await exportMapPdf(
      mapRef.value,
      geojsonData.value,
      projectName.value,
      preferences.value.coordinateFormat,
    )
    appInfo.value = "PDF print dialog opened. Use your browser's 'Save as PDF' option."
  } catch (err) {
    appError.value = `PDF export failed: ${err?.message || "unknown error"}`
    appInfo.value = ""
  }
}

async function loadDemoKml() {
  appError.value = ""
  appInfo.value = ""

  try {
    const res = await fetch(DEMO_KML_URL, { cache: "no-store" })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const text = await res.text()
    const normalized = parseImportedText(text, "demo-magpie.kml")
    if (normalized.features.length > FEATURE_CAP) {
      appError.value = `Demo KML has ${normalized.features.length} features, above cap (${FEATURE_CAP}).`
      return
    }
    geojsonData.value = normalized
    projectName.value = "demo-magpie"
    renderGeoJsonToMap()
    if (normalized.features.length) onZoomToFeature(normalized.features[0])
    appInfo.value = `Loaded demo-magpie.kml (${normalized.features.length} features).`
  } catch (err) {
    appError.value = `Failed to load demo KML: ${err?.message || "unknown error"}`
  }
}

function sanitizeProjectName(input) {
  const clean = String(input || "magpie-project").trim().replace(/[^a-zA-Z0-9-_ ]/g, "")
  return clean ? clean.replace(/\s+/g, "-") : "magpie-project"
}

function downloadText(text, filename, mime) {
  const blob = new Blob([text], { type: mime })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

function ensureGeoJsonLayers(map) {
  if (!map.getSource("draw-source")) {
    map.addSource("draw-source", { type: "geojson", data: geojsonData.value })
  }

  if (!map.getLayer("draw-polygons-fill")) {
    map.addLayer({
      id: "draw-polygons-fill",
      type: "fill",
      source: "draw-source",
      filter: ["in", ["geometry-type"], ["literal", ["Polygon", "MultiPolygon"]]],
      paint: {
        "fill-color": ["coalesce", ["get", "color"], "#0080ff"],
        "fill-opacity": ["coalesce", ["get", "opacity"], 0.25],
      },
    })
  }

  if (!map.getLayer("draw-polygons-outline")) {
    map.addLayer({
      id: "draw-polygons-outline",
      type: "line",
      source: "draw-source",
      filter: ["in", ["geometry-type"], ["literal", ["Polygon", "MultiPolygon"]]],
      paint: {
        "line-color": ["coalesce", ["get", "color"], "#0080ff"],
        "line-width": 2,
      },
    })
  }

  if (!map.getLayer("draw-lines")) {
    map.addLayer({
      id: "draw-lines",
      type: "line",
      source: "draw-source",
      filter: ["in", ["geometry-type"], ["literal", ["LineString", "MultiLineString"]]],
      paint: {
        "line-color": ["coalesce", ["get", "color"], "#0080ff"],
        "line-width": 3,
      },
    })
  }

  if (!map.getLayer("draw-points-circles")) {
    map.addLayer({
      id: "draw-points-circles",
      type: "circle",
      source: "draw-source",
      filter: ["in", ["geometry-type"], ["literal", ["Point", "MultiPoint"]]],
      paint: {
        "circle-radius": 7,
        "circle-color": ["coalesce", ["get", "color"], "#000000"],
        "circle-stroke-width": 2,
        "circle-stroke-color": "#ffffff",
      },
    })
  }

  if (!map.getLayer("draw-points-icons")) {
    map.addLayer({
      id: "draw-points-icons",
      type: "symbol",
      source: "draw-source",
      filter: ["all", ["in", ["geometry-type"], ["literal", ["Point", "MultiPoint"]]], ["has", "iconId"]],
      layout: {
        "icon-image": ["get", "iconId"],
        "icon-size": 0.9,
        "icon-allow-overlap": true,
      },
    })
  }

  if (!map.getLayer("draw-point-names")) {
    map.addLayer({
      id: "draw-point-names",
      type: "symbol",
      source: "draw-source",
      filter: ["all", ["in", ["geometry-type"], ["literal", ["Point", "MultiPoint"]]], ["has", "name"]],
      layout: {
        "text-field": ["get", "name"],
        "text-size": 12,
        "text-anchor": "top",
        "text-offset": [0, 1.1],
        "text-allow-overlap": true,
      },
      paint: {
        "text-color": "#111111",
        "text-halo-color": "#ffffff",
        "text-halo-width": 1,
      },
    })
  }

  if (!map.getSource("draw-label-source")) {
    map.addSource("draw-label-source", { type: "geojson", data: emptyFeatureCollection() })
  }

  if (!map.getLayer("draw-polygon-names")) {
    map.addLayer({
      id: "draw-polygon-names",
      type: "symbol",
      source: "draw-label-source",
      filter: ["all", ["==", ["geometry-type"], "Point"], ["==", ["get", "labelType"], "polygon"]],
      layout: {
        "text-field": ["get", "name"],
        "text-size": 12,
        "text-allow-overlap": true,
      },
      paint: {
        "text-color": "#111111",
        "text-halo-color": "#ffffff",
        "text-halo-width": 1,
      },
    })
  }

  if (!map.getLayer("draw-points-symbol-fallback")) {
    map.addLayer({
      id: "draw-points-symbol-fallback",
      type: "symbol",
      source: "draw-source",
      filter: [
        "all",
        ["in", ["geometry-type"], ["literal", ["Point", "MultiPoint"]]],
        ["has", "iconSymbol"],
        ["!", ["has", "iconId"]],
      ],
      layout: {
        "text-field": ["get", "iconSymbol"],
        "text-size": 13,
        "text-allow-overlap": true,
      },
      paint: {
        "text-color": "#111111",
        "text-halo-color": "#ffffff",
        "text-halo-width": 1,
      },
    })
  }

  if (!map.getSource("draw-draft-source")) {
    map.addSource("draw-draft-source", { type: "geojson", data: emptyFeatureCollection() })
  }

  if (!map.getLayer("draw-draft-line")) {
    map.addLayer({
      id: "draw-draft-line",
      type: "line",
      source: "draw-draft-source",
      filter: ["==", ["geometry-type"], "LineString"],
      paint: { "line-color": "#ff7a00", "line-width": 2, "line-dasharray": [2, 2] },
    })
  }

  if (!map.getLayer("draw-draft-points")) {
    map.addLayer({
      id: "draw-draft-points",
      type: "circle",
      source: "draw-draft-source",
      filter: ["==", ["geometry-type"], "Point"],
      paint: { "circle-radius": 4, "circle-color": "#ff7a00" },
    })
  }
}

function renderDraftToMap() {
  renderGeoJsonToMap()
}

function writeDraftToMap(map, draftSnapshot, generation) {
  if (!ownsMapRender(map, generation)) return
  const source = map.getSource("draw-draft-source")
  if (!source || !ownsMapRender(map, generation)) return

  const points = draftSnapshot.map((c) => ({ type: "Feature", properties: {}, geometry: { type: "Point", coordinates: c } }))
  const features = [...points]
  if (draftSnapshot.length >= 2) {
    features.push({ type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: draftSnapshot } })
  }

  if (ownsMapRender(map, generation)) {
    source.setData({ type: "FeatureCollection", features })
  }
}

function cloneFeatureCollection(fc) {
  return JSON.parse(JSON.stringify(fc))
}

function iconIdFromUrl(url) {
  const s = String(url || "").trim()
  if (!s) return ""
  let hash = 0
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) >>> 0
  return `kml-icon-${hash.toString(16)}`
}

function loadMapImage(map, url) {
  return new Promise((resolve) => {
    const httpsUrl = String(url || "").replace(/^http:\/\//i, "https://")
    map.loadImage(httpsUrl, (err, image) => {
      if (err || !image) return resolve(null)
      resolve(image)
    })
  })
}

async function prepareFeatureCollectionForMap(map, fc, ownsRender) {
  const out = cloneFeatureCollection(fc)
  let failed = 0

  for (const feature of out.features) {
    if (feature?.geometry?.type !== "Point") continue
    const iconUrl = feature?.properties?.iconUrl
    if (!iconUrl) continue

    const iconId = iconIdFromUrl(iconUrl)
    if (!iconId) continue

    if (!map.hasImage(iconId)) {
      const img = await loadMapImage(map, iconUrl)
      if (!ownsRender()) return null
      if (img && !map.hasImage(iconId)) {
        if (!ownsRender()) return null
        map.addImage(iconId, img)
      }
    }

    if (map.hasImage(iconId)) {
      feature.properties = { ...feature.properties, iconId, iconStatus: "ok" }
    } else if (feature.properties) {
      delete feature.properties.iconId
      feature.properties.iconStatus = "fallback"
      failed += 1
    }
  }

  return { featureCollection: out, failureCount: failed }
}

function onZoomToFeature(feature) {
  const map = mapRef.value
  if (!map) return
  const bounds = featureBounds(feature)
  if (!bounds) return
  map.fitBounds(bounds, { padding: 60, maxZoom: 16 })
}

function zoomToFeatures(features) {
  const map = mapRef.value
  if (!map || !readyMaps.has(map) || !Array.isArray(features) || !features.length) return
  if (features.length === 1) {
    onZoomToFeature(features[0])
    return
  }

  const coordinates = features.flatMap(featureCoordinates)
  if (!coordinates.length) return

  let minLng = 180
  let minLat = 90
  let maxLng = -180
  let maxLat = -90
  for (const [lng, lat] of coordinates) {
    minLng = Math.min(minLng, lng)
    minLat = Math.min(minLat, lat)
    maxLng = Math.max(maxLng, lng)
    maxLat = Math.max(maxLat, lat)
  }
  map.fitBounds(
    [[minLng, minLat], [maxLng, maxLat]],
    { padding: 48, maxZoom: 14 },
  )
}

function onDeleteFeature(featureOrPayload) {
  const id = featureOrPayload?.id ?? featureOrPayload?.properties?.id ?? featureOrPayload?.__id
  if (id == null) return

  const fc = cloneFeatureCollection(geojsonData.value)
  fc.features = fc.features.filter((f) => String(f.id ?? f.properties?.id ?? "") !== String(id))
  geojsonData.value = fc
  renderGeoJsonToMap()
}

function featureBounds(feature) {
  const coords = featureCoordinates(feature)
  if (!coords.length) return null

  let minLng = 180,
    minLat = 90,
    maxLng = -180,
    maxLat = -90
  for (const [lng, lat] of coords) {
    minLng = Math.min(minLng, lng)
    minLat = Math.min(minLat, lat)
    maxLng = Math.max(maxLng, lng)
    maxLat = Math.max(maxLat, lat)
  }
  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ]
}

function featureCoordinates(feature) {
  return extractAllCoords(feature?.geometry).filter(
    (coordinate) =>
      Array.isArray(coordinate) &&
      coordinate.length >= 2 &&
      Number.isFinite(coordinate[0]) &&
      Number.isFinite(coordinate[1]),
  )
}

function extractAllCoords(geom) {
  if (!geom) return []
  const t = geom.type
  const c = geom.coordinates
  if (t === "Point") return [c]
  if (t === "LineString") return Array.isArray(c) ? c : []
  if (t === "Polygon") return Array.isArray(c) ? c.flat() : []
  if (t === "MultiPoint") return Array.isArray(c) ? c : []
  if (t === "MultiLineString") return Array.isArray(c) ? c.flat() : []
  if (t === "MultiPolygon") return Array.isArray(c) ? c.flat(2) : []
  if (t === "GeometryCollection") {
    return Array.isArray(geom.geometries) ? geom.geometries.flatMap(extractAllCoords) : []
  }
  return []
}

function polygonLabelPoint(feature) {
  const g = feature?.geometry
  if (!g) return null

  if (feature?.properties?.featureType === "circle" || feature?.properties?.featureType === "sector") {
    const lng = Number(feature?.properties?.centerLng)
    const lat = Number(feature?.properties?.centerLat)
    if (Number.isFinite(lng) && Number.isFinite(lat)) return [lng, lat]
  }

  let ring = null
  if (g.type === "Polygon") ring = g.coordinates?.[0]
  if (g.type === "MultiPolygon") ring = g.coordinates?.[0]?.[0]
  if (!ring || ring.length < 3) return null

  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity
  for (const [lng, lat] of ring) {
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue
    minLng = Math.min(minLng, lng)
    minLat = Math.min(minLat, lat)
    maxLng = Math.max(maxLng, lng)
    maxLat = Math.max(maxLat, lat)
  }
  if (!Number.isFinite(minLng)) return null
  return [(minLng + maxLng) / 2, (minLat + maxLat) / 2]
}

function buildLabelFeatureCollection(fc) {
  const labels = []
  for (const f of fc.features || []) {
    const name = f?.properties?.name
    if (!name) continue
    const t = f?.geometry?.type
    if (t !== "Polygon" && t !== "MultiPolygon") continue
    const c = polygonLabelPoint(f)
    if (!c) continue
    labels.push({
      type: "Feature",
      properties: { name, labelType: "polygon" },
      geometry: { type: "Point", coordinates: c },
    })
  }
  return { type: "FeatureCollection", features: labels }
}

function onStartPolygonDraw() {
  manualPolygonActive.value = true
  manualPolygonDraft.value = []
  appInfo.value = "Manual polygon draw started. Click map to add vertices, then Finish draw."
  renderDraftToMap()
}

function onCancelPolygonDraw() {
  manualPolygonActive.value = false
  manualPolygonDraft.value = []
  appInfo.value = "Manual polygon draw cancelled."
  renderDraftToMap()
}

function onFinishPolygonDraw(payload) {
  if (!manualPolygonActive.value) return
  if (manualPolygonDraft.value.length < 3) {
    appError.value = "Manual polygon draw needs at least 3 vertices."
    return
  }

  const ring = [...manualPolygonDraft.value]
  if (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1]) ring.push(ring[0])

  onAddFeature({
    type: "Feature",
    id: crypto?.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()),
    properties: {
      featureType: "polygon",
      name: String(payload?.name || "Polygon (map draw)").trim() || "Polygon (map draw)",
      description: String(payload?.description || ""),
      color: payload?.color || "#00aa66",
      opacity: Math.min(1, Math.max(0, Number(payload?.opacity ?? 0.25))),
    },
    geometry: { type: "Polygon", coordinates: [ring] },
  })

  manualPolygonActive.value = false
  manualPolygonDraft.value = []
  renderDraftToMap()
  appInfo.value = "Manual polygon created from map clicks."
}

function onAddFeature(feature) {
  appError.value = ""
  if (!feature || feature.type !== "Feature") return
  if (geojsonData.value.features.length >= FEATURE_CAP) {
    appError.value = `Feature cap reached (${FEATURE_CAP}). Delete an item before adding another.`
    return
  }

  onAddFeatures([feature])
}

function onAddFeatures(features, acknowledge) {
  appError.value = ""
  const result = appendFeatureCollection(geojsonData.value, features, {
    featureCap: FEATURE_CAP,
  })
  if (!result.ok) {
    appError.value = result.error
    if (typeof acknowledge === "function") acknowledge({ ok: false, error: result.error })
    return false
  }
  if (!result.addedFeatures.length) {
    if (typeof acknowledge === "function") acknowledge({ ok: true, addedCount: 0 })
    return true
  }

  geojsonData.value = result.featureCollection
  renderGeoJsonToMap()
  zoomToFeatures(result.addedFeatures)
  if (typeof acknowledge === "function") {
    acknowledge({ ok: true, addedCount: result.addedFeatures.length })
  }
  return true
}

function onUpdateFeature({ id, updates, geometry }) {
  if (!id) return

  const fc = cloneFeatureCollection(geojsonData.value)
  fc.features = fc.features.map((f) => {
    const fid = String(f.id ?? f.properties?.id ?? "")
    if (fid !== String(id)) return f

    return {
      ...f,
      properties: { ...f.properties, ...updates },
      geometry: geometry || f.geometry,
    }
  })

  geojsonData.value = fc
  renderGeoJsonToMap()
}

function getPolygonOuterRings(feature) {
  const geometry = feature?.geometry
  if (!geometry) return []
  if (geometry.type === "Polygon") return geometry.coordinates?.[0] ? [geometry.coordinates[0]] : []
  if (geometry.type === "MultiPolygon") return (geometry.coordinates || []).map((poly) => poly?.[0]).filter(Boolean)
  return []
}

function sameCoord(a, b) {
  return Array.isArray(a) && Array.isArray(b) && a.length >= 2 && b.length >= 2 && a[0] === b[0] && a[1] === b[1]
}

function compactRingVertices(ring) {
  if (!Array.isArray(ring)) return []
  const vertices = ring.filter((c) => Array.isArray(c) && c.length >= 2)
  if (vertices.length > 1 && sameCoord(vertices[0], vertices[vertices.length - 1])) return vertices.slice(0, -1)
  return vertices
}

function polygonVertexPrefix(name) {
  const letters = String(name || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")

  if (!letters.length) return "POI"
  const x = letters[0]
  const y = letters.length >= 2 ? letters[letters.length - 2] : x
  const z = letters[letters.length - 1]
  return `${x}${y}${z}`
}

function addVerticesTool() {
  appError.value = ""
  appInfo.value = ""

  const source = geojsonData.value.features || []
  const polygonEntries = []

  for (const feature of source) {
    if (feature?.properties?.featureType === "circle" || feature?.properties?.featureType === "sector") continue
    const polygonName = String(feature?.properties?.name || "Polygon").trim() || "Polygon"
    for (const ring of getPolygonOuterRings(feature)) {
      const vertices = compactRingVertices(ring)
      if (!vertices.length) continue
      polygonEntries.push({ polygonName, vertices })
    }
  }

  if (!polygonEntries.length) {
    appInfo.value = "No polygons found. Add a polygon first."
    return
  }

  const availableSlots = FEATURE_CAP - source.length
  const requiredPoints = polygonEntries.reduce((sum, p) => sum + p.vertices.length, 0)
  if (requiredPoints > availableSlots) {
    appError.value = `Add Vertices would create ${requiredPoints} points, but only ${availableSlots} feature slots remain (cap ${FEATURE_CAP}).`
    return
  }

  const fc = cloneFeatureCollection(geojsonData.value)
  let totalAdded = 0

  polygonEntries.forEach((poly, polyIndex) => {
    const colorKey = PADDLE_BLANK_ICON_COLORS[polyIndex % PADDLE_BLANK_ICON_COLORS.length]
    const iconUrl = `https://maps.google.com/mapfiles/kml/paddle/${colorKey}-circle.png`
    const iconLabel = `${colorKey.toUpperCase()} blank`
    const prefix = polygonVertexPrefix(poly.polygonName)

    poly.vertices.forEach(([lng, lat], vertexIndex) => {
      const seq = String(vertexIndex + 1).padStart(2, "0")
      fc.features.push({
        type: "Feature",
        id: crypto?.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()),
        properties: {
          featureType: "marker",
          name: `${prefix}${seq}`,
          description: `Vertex ${seq} of ${poly.polygonName}`,
          color: "#000000",
          iconUrl,
          iconSymbol: "•",
          iconLabel,
        },
        geometry: { type: "Point", coordinates: [lng, lat] },
      })
      totalAdded += 1
    })
  })

  geojsonData.value = fc
  renderGeoJsonToMap()
  appInfo.value = `Added ${totalAdded} vertex point${totalAdded === 1 ? "" : "s"} from ${polygonEntries.length} polygon${polygonEntries.length === 1 ? "" : "s"}.`
}

function removeDuplicatePointsTool() {
  appError.value = ""
  appInfo.value = ""

  const pointCount = (geojsonData.value.features ?? []).filter(
    (f) => f?.geometry?.type === "Point"
  ).length

  if (pointCount === 0) {
    appInfo.value = "No point features found. Add some points first."
    return
  }

  const { fc, removed } = removeDuplicatePoints(geojsonData.value)

  if (removed === 0) {
    appInfo.value = `No duplicate points found (${pointCount} point${pointCount === 1 ? "" : "s"} checked).`
    return
  }

  geojsonData.value = fc
  renderGeoJsonToMap()
  appInfo.value = `Removed ${removed} duplicate point${removed === 1 ? "" : "s"} (${pointCount - removed} of ${pointCount} kept).`
}

function onDeleteGroup(groupKey) {
  const fc = cloneFeatureCollection(geojsonData.value)

  fc.features = fc.features.filter((f) => {
    const t = f?.geometry?.type
    if (groupKey === "points") return !(t === "Point" || t === "MultiPoint")
    if (groupKey === "lines") return !(t === "LineString" || t === "MultiLineString")
    if (groupKey === "polygons") return !(t === "Polygon" || t === "MultiPolygon")
    if (groupKey === "other") {
      return t === "Point" || t === "MultiPoint" || t === "LineString" || t === "MultiLineString" || t === "Polygon" || t === "MultiPolygon"
    }
    return true
  })

  geojsonData.value = fc
  renderGeoJsonToMap()
}
</script>

<template>
  <header-bar title="MAGPIE" :description="randomTagline" />
  <menu-bar @menu-action="handleMenuAction" />
  <TutorialTour
    v-if="showTutorial"
    :steps="TUTORIAL_STEPS"
    @close="showTutorial = false"
  />
  <coordinate-converter-dialog
    v-if="showCoordinateConverter"
    :coordinate-format="preferences.coordinateFormat"
    @close="showCoordinateConverter = false"
  />
  <settings-dialog
    v-if="showSettings"
    :coordinate-format="preferences.coordinateFormat"
    :error="settingsError"
    @save="commitSettings"
    @close="closeSettings"
  />

  <div class="project-row">
    <label for="project-name">Project name</label>
    <input id="project-name" v-model="projectName" type="text" placeholder="magpie-project" />
    <button
      class="theme-toggle btn"
      type="button"
      :aria-label="`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`"
      title="Toggle dark mode"
      @click="toggleTheme"
    >
      {{ theme === "dark" ? "☀︎" : "☾" }}
    </button>
  </div>

  <p v-if="appError" class="banner error" role="alert">{{ appError }}</p>
  <p v-else-if="appInfo" class="banner info" role="status" aria-live="polite">{{ appInfo }}</p>
  <p v-if="duplicatePointCount > 0" class="banner warn" role="status" aria-live="polite">Duplicate points detected: {{ duplicatePointCount }} (flagged only, not auto-removed).</p>
  <p v-if="iconLoadFailures > 0" class="banner warn" role="status" aria-live="polite">{{ iconLoadFailures }} point icon(s) could not be loaded from remote URL; fallback symbol/circle is shown.</p>

  <input
    ref="fileInputRef"
    id="open-file-input"
    data-test="open-file-input"
    type="file"
    accept=".kml,.geojson,.json,application/vnd.google-earth.kml+xml,application/geo+json,application/json"
    style="display: none"
    @change="onFileChosen"
  />
  <input
    ref="importFileInputRef"
    id="import-file-input"
    data-test="import-file-input"
    type="file"
    accept=".kml,.geojson,.json,application/vnd.google-earth.kml+xml,application/geo+json,application/json"
    style="display: none"
    @change="onImportFileChosen"
  />

  <div v-if="showAboutModal" class="modal-backdrop" @click="closeAbout">
    <div class="modal" role="dialog" aria-modal="true" aria-labelledby="about-title" @click.stop>
      <h3 id="about-title">About Magpie</h3>
      <p><strong>Contact:</strong> <a :href="`mailto:${aboutEmail}`">{{ aboutEmail }}</a></p>
      <p class="build">Version {{ buildVersion }}</p>
      <p class="build">Build date {{ buildDate }}</p>
      <p class="about-description">Magpie is a browser-based geospatial overlay editor for creating, importing, editing, and exporting mapping data in KML, GeoJSON, CSV, and PDF formats. It supports thirteen coordinate representations—including DD, DDM, DMS, radians, UTM, and MGRS—and accepts practical variations in spacing, symbols, and hemisphere notation. Project data is processed locally in the browser and is not stored or transmitted by Magpie.</p>
      <div class="about-samples">
        <p class="about-samples-title"><strong>Working coordinate input samples</strong></p>
        <ul class="about-samples-list">
          <li v-for="sample in coordinateFormatSamples" :key="sample.label">
            <strong>{{ sample.label }}:</strong> <code>{{ sample.value }}</code>
          </li>
        </ul>
      </div>
      <div class="row-actions">
        <button ref="modalCloseRef" class="secondary" type="button" @click="closeAbout">Close</button>
      </div>
    </div>
  </div>

  <div class="layout">
    <LeftBar
      :feature-cap="FEATURE_CAP"
      :current-feature-count="featureCount"
      :manual-draw-active="manualPolygonActive"
      :manual-draw-vertex-count="manualPolygonDraft.length"
      @add-feature="onAddFeature"
      @add-features="onAddFeatures"
      @start-polygon-draw="onStartPolygonDraw"
      @finish-polygon-draw="onFinishPolygonDraw"
      @cancel-polygon-draw="onCancelPolygonDraw"
    />
    <MapBox :center="[151.7801, -32.9283]" :zoom="11" @loaded="onMapLoaded" />
    <RightBar
      :featureCollection="geojsonData"
      :coordinate-format="preferences.coordinateFormat"
      @zoom-to-feature="onZoomToFeature"
      @delete-feature="onDeleteFeature"
      @delete-group="onDeleteGroup"
      @update-feature="onUpdateFeature"
    />
  </div>
</template>

<style scoped>
.layout {
  flex: 1 1 auto;
  min-height: 0;
  display: grid;
  grid-template-columns: var(--sidebar-width) minmax(0, 1fr) var(--sidebar-width);
}
.layout > :deep(*) {
  min-height: 0;
  height: 100%;
}
.project-row {
  display: flex;
  gap: var(--space-3);
  align-items: center;
  padding: var(--space-2) var(--space-3);
  background: var(--color-bg-subtle);
  border-bottom: 1px solid var(--color-border);
  font-size: var(--fs-sm);
}
.project-row label {
  color: var(--color-text-muted);
  font-size: var(--fs-sm);
}
.project-row input {
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-md);
  padding: var(--space-2) var(--space-3);
  min-width: 240px;
  background: var(--color-bg-raised);
  color: var(--color-text);
  font-size: var(--fs-sm);
}
.theme-toggle {
  margin-left: auto;
  width: 34px;
  height: 30px;
  display: grid;
  place-items: center;
  font-size: var(--fs-md);
  line-height: 1;
}
.banner {
  margin: 0;
  padding: var(--space-2) var(--space-3);
  font-size: var(--fs-sm);
}
.banner.error {
  background: var(--color-error-bg);
  color: var(--color-error-text);
}
.banner.info {
  background: var(--color-info-bg);
  color: var(--color-info-text);
}
.banner.warn {
  background: var(--color-warn-bg);
  color: var(--color-warn-text);
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: var(--color-overlay);
  z-index: 1200;
  display: grid;
  place-items: center;
}

.modal {
  width: min(420px, 92vw);
  max-height: 90vh;
  overflow-y: auto;
  background: var(--color-bg-raised);
  color: var(--color-text);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: var(--space-4);
  box-shadow: var(--shadow-lg);
}

.modal h3 { margin: 0 0 var(--space-3); }
.modal p { margin: var(--space-2) 0; font-size: var(--fs-md); }
.modal .build { color: var(--color-text-muted); font-size: var(--fs-xs); }
.modal a { color: var(--color-accent); }
.modal .about-description {
  margin-top: var(--space-3);
  color: var(--color-text);
  font-size: var(--fs-sm);
  line-height: var(--lh-normal);
}
.about-samples {
  margin-top: var(--space-3);
  padding-top: var(--space-3);
  border-top: 1px solid var(--color-border);
}
.about-samples-title {
  margin: 0 0 var(--space-2);
  font-size: var(--fs-sm);
}
.about-samples-list {
  margin: 0;
  padding-left: 18px;
  font-size: var(--fs-xs);
  line-height: var(--lh-normal);
  color: var(--color-text-muted);
}
.about-samples-list code {
  font-family: var(--font-mono);
  font-size: var(--fs-xs);
  color: var(--color-text);
}
.row-actions { margin-top: var(--space-3); display: flex; justify-content: flex-end; }
.secondary {
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  cursor: pointer;
  border: 1px solid var(--color-border-strong);
  background: var(--color-bg-raised);
  color: var(--color-text);
  font-size: var(--fs-sm);
}
.secondary:hover { background: var(--color-bg-subtle); }

/* Responsive: stack sidebars over the map on narrow viewports */
@media (max-width: 900px) {
  .layout {
    grid-template-columns: 1fr;
    grid-template-rows: auto minmax(240px, 1fr) auto;
    overflow-y: auto;
  }
}
</style>
